import { useEffect, useMemo } from 'react'
import { useBackgroundJobsPoller } from '@/hooks/useBackgroundJobsPoller'
import { createPortal } from 'react-dom'
import {
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import type { BackgroundJob } from '@/services/jobs'
import { backgroundJobHasErrors, pendingQueuePosition } from '@/services/jobs'
import {
  useBackgroundJobsStore,
  type ServerJobRecord,
  type SyntheticJobRecord,
} from '@/store/backgroundJobsStore'

type StackItem =
  | { kind: 'server'; record: ServerJobRecord }
  | { kind: 'synthetic'; record: SyntheticJobRecord }

function serverTier(job: BackgroundJob): number {
  if (job.status === 'pending' || job.status === 'running') return 0
  if (
    job.status === 'failed' ||
    job.status === 'completed_with_errors' ||
    backgroundJobHasErrors(job)
  ) {
    return 1
  }
  return 2
}

function syntheticTier(s: SyntheticJobRecord): number {
  if (s.status === 'running') return 0
  if (s.status === 'error') return 1
  return 2
}

function sortStackItems(items: StackItem[]): StackItem[] {
  return [...items].sort((a, b) => {
    const ta = a.kind === 'server' ? serverTier(a.record.job) : syntheticTier(a.record)
    const tb = b.kind === 'server' ? serverTier(b.record.job) : syntheticTier(b.record)
    if (ta !== tb) return ta - tb
    const ca = a.kind === 'server' ? a.record.createdAt : a.record.createdAt
    const cb = b.kind === 'server' ? b.record.createdAt : b.record.createdAt
    return cb - ca
  })
}

function bulkEmailProcessWasPureSkip(job: BackgroundJob): boolean {
  return (
    job.job_type === 'bulk_email_process' &&
    job.status === 'completed' &&
    job.successful_items === 0 &&
    job.failed_items === 0
  )
}

function serverJobHeadline(job: BackgroundJob, allJobs: BackgroundJob[]): string {
  if (job.status === 'pending') {
    const queuePos = pendingQueuePosition(job, allJobs)
    if (queuePos != null) {
      return queuePos === 1 ? 'Waiting…' : `Waiting… (#${queuePos} in queue)`
    }
    return 'Starting…'
  }
  if (job.status === 'running') {
    if (job.job_type === 'bulk_upload') return 'Processing uploads'
    if (job.job_type === 'bulk_reprocess') return 'Re-running extraction'
    if (job.job_type === 'bulk_approve') return 'Sending to QBO'
    if (job.job_type === 'bulk_email_process') return 'Processing emails'
    if (job.job_type === 'bulk_allocate_shared_costs') return 'Allocating shared costs'
    if (job.job_type === 'bulk_refresh_categories') return 'Refreshing categories'
    if (job.job_type === 'clover_backfill_apply') return 'Applying Clover JEs'
    return 'Processing'
  }
  if (job.status === 'failed') return 'Failed'
  if (job.status === 'completed_with_errors') return 'Completed with errors'
  if (backgroundJobHasErrors(job)) return 'Completed with errors'
  if (bulkEmailProcessWasPureSkip(job)) {
    const skippedCount = job.result_data?.skipped_emails?.length ?? job.total_items
    return skippedCount === 1 ? 'Already in BookKeeper' : 'No new documents'
  }
  return 'Completed'
}

function serverJobBarTone(job: BackgroundJob): 'red' | 'amber' | 'blue' {
  if (job.status === 'failed') return 'red'
  if (job.failed_items > 0) return 'amber'
  if (bulkEmailProcessWasPureSkip(job)) return 'amber'
  return 'blue'
}

function pct(job: BackgroundJob): number {
  if (job.total_items === 0) return 0
  return Math.round((job.processed_items / job.total_items) * 100) || 0
}

function ServerJobCard({
  record,
  allJobs,
}: {
  record: ServerJobRecord
  allJobs: BackgroundJob[]
}) {
  const job = record.job
  const removeServerJob = useBackgroundJobsStore((s) => s.removeServerJob)

  const shouldAutoDismiss =
    job.status === 'completed_with_errors' ||
    ((job.status === 'completed' || job.status === 'failed') && !backgroundJobHasErrors(job))

  useEffect(() => {
    if (!shouldAutoDismiss) return
    const t = window.setTimeout(() => removeServerJob(job.id), 5000)
    return () => clearTimeout(t)
  }, [job.id, shouldAutoDismiss, removeServerJob])

  const showDismiss =
    job.status === 'completed' ||
    job.status === 'completed_with_errors' ||
    job.status === 'failed' ||
    backgroundJobHasErrors(job)

  return (
    <div
      className="w-full max-md:max-h-[min(58vh,26rem)] sm:w-80 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden flex flex-col"
      role="status"
      aria-live="polite"
    >
      <div className="flex-shrink-0 p-3 border-b bg-gray-50 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {job.status === 'running' || job.status === 'pending' ? (
            <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
          ) : job.status === 'failed' ? (
            <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
          ) : job.status === 'completed_with_errors' ||
            backgroundJobHasErrors(job) ||
            bulkEmailProcessWasPureSkip(job) ? (
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
          ) : (
            <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
          )}
          <span className="font-medium text-sm text-gray-800 truncate">
            {serverJobHeadline(job, allJobs)}
          </span>
        </div>
        {showDismiss && (
          <button
            type="button"
            onClick={() => removeServerJob(job.id)}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 flex-shrink-0 touch-manipulation"
            aria-label="Dismiss job status"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="p-3 max-md:flex-1 max-md:min-h-0 max-md:overflow-y-auto">
        <div className="flex justify-between text-xs text-gray-600 mb-1.5">
          <span>
            {job.processed_items} of {job.total_items}
          </span>
          <span>{pct(job)}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              serverJobBarTone(job) === 'red'
                ? 'bg-red-500'
                : serverJobBarTone(job) === 'amber'
                  ? 'bg-amber-500'
                  : 'bg-blue-500'
            }`}
            style={{
              width: `${job.total_items === 0 ? 0 : (job.processed_items / job.total_items) * 100}%`,
            }}
          />
        </div>
        {job.error_message && (
          <p className="mt-2 text-xs text-red-700 font-medium">{job.error_message}</p>
        )}
        {(job.result_data?.unprocessed_emails?.length ?? 0) > 0 && job.job_type === 'bulk_email_process' && (
          <button
            type="button"
            className="mt-2 w-full text-xs py-1.5 px-2 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
            onClick={() => {
              const lines = job.result_data!.unprocessed_emails!.map(
                (e) => `${e.inbox}: ${e.gmail_id}`
              )
              void navigator.clipboard.writeText(lines.join('\n'))
            }}
          >
            Copy unprocessed emails (inbox + Gmail id)
          </button>
        )}
        {(job.status === 'completed' ||
          job.status === 'completed_with_errors' ||
          job.status === 'failed') && (
          <div className="mt-2 text-xs space-y-0.5">
            {bulkEmailProcessWasPureSkip(job) ? (
              <p className="text-amber-600">
                {(job.result_data?.skipped_emails?.length ?? job.total_items) === 1
                  ? 'Already in BookKeeper — nothing added to Doc Review'
                  : `${job.result_data?.skipped_emails?.length ?? job.total_items} already in BookKeeper — nothing added to Doc Review`}
              </p>
            ) : (
              <>
                {job.successful_items > 0 && (
                  <p className="text-green-600">{job.successful_items} processed</p>
                )}
                {job.failed_items > 0 && <p className="text-red-600">{job.failed_items} failed</p>}
              </>
            )}
          </div>
        )}
        {job.result_data?.failed_emails && job.result_data.failed_emails.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-medium text-gray-700 mb-1">Errors:</p>
            {job.result_data.failed_emails.map(
              (email: { inbox: string; error: string }, idx: number) => (
                <div key={idx} className="text-xs text-red-600 py-0.5">
                  {email.inbox}: {email.error}
                </div>
              )
            )}
          </div>
        )}
        {job.result_data?.failed_docs && job.result_data.failed_docs.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-medium text-gray-700 mb-1">Errors:</p>
            {job.result_data.failed_docs.map((doc, idx) => (
              <div
                key={`${doc.id ?? ''}-${doc.filename ?? ''}-${idx}`}
                className="text-xs text-red-600 py-0.5"
              >
                {doc.filename || doc.reference_number || `#${doc.id}`}: {doc.error}
              </div>
            ))}
          </div>
        )}
        {job.job_type === 'bulk_upload' &&
          (job.result_data?.unprocessed_filenames?.length ?? 0) > 0 && (
            <button
              type="button"
              className="mt-2 w-full text-xs py-1.5 px-2 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
              onClick={() => {
                const lines = job.result_data!.unprocessed_filenames!
                void navigator.clipboard.writeText(lines.join('\n'))
              }}
            >
              Copy unprocessed filenames
            </button>
          )}
      </div>
    </div>
  )
}

function SyntheticJobCard({ record }: { record: SyntheticJobRecord }) {
  const removeSynthetic = useBackgroundJobsStore((s) => s.removeSynthetic)

  useEffect(() => {
    if (record.status !== 'success') return
    const t = window.setTimeout(() => removeSynthetic(record.id), 5000)
    return () => clearTimeout(t)
  }, [record.id, record.status, removeSynthetic])

  return (
    <div
      className="w-full sm:w-80 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div className="flex-shrink-0 p-3 border-b bg-gray-50 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {record.status === 'running' ? (
            <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
          ) : record.status === 'error' ? (
            <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
          ) : (
            <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
          )}
          <span className="font-medium text-sm text-gray-800 truncate">{record.title}</span>
        </div>
        {(record.status === 'success' || record.status === 'error') && (
          <button
            type="button"
            onClick={() => removeSynthetic(record.id)}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 flex-shrink-0 touch-manipulation"
            aria-label="Dismiss"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="p-3 text-xs text-gray-700">
        {record.status === 'running' && <p>Please wait…</p>}
        {record.status === 'success' && record.message && (
          <p className="text-green-700">{record.message}</p>
        )}
        {record.status === 'error' && record.error && (
          <p className="text-red-700 font-medium">{record.error}</p>
        )}
      </div>
    </div>
  )
}

type JobToastStackProps = {
  /** When true, render cards only (no portal shell). Used inside BottomRightNotificationStack. */
  embedded?: boolean
}

export default function JobToastStack({ embedded = false }: JobToastStackProps) {
  useBackgroundJobsPoller()

  const serverById = useBackgroundJobsStore((s) => s.serverById)
  const synthetics = useBackgroundJobsStore((s) => s.synthetics)

  const items = useMemo(() => {
    const list: StackItem[] = [
      ...Object.values(serverById).map((record) => ({ kind: 'server' as const, record })),
      ...synthetics.map((record) => ({ kind: 'synthetic' as const, record })),
    ]
    return sortStackItems(list)
  }, [serverById, synthetics])

  const allServerJobs = useMemo(
    () => Object.values(serverById).map((r) => r.job),
    [serverById]
  )

  if (items.length === 0) return null

  const content = (
    <>
      {items.map((item) =>
        item.kind === 'server' ? (
          <ServerJobCard key={`server-${item.record.job.id}`} record={item.record} allJobs={allServerJobs} />
        ) : (
          <SyntheticJobCard key={`syn-${item.record.id}`} record={item.record} />
        )
      )}
    </>
  )

  if (embedded) return content

  const shell = (
    <div
      className="fixed z-50 flex flex-col gap-2 max-md:left-3 max-md:right-3 max-md:bottom-[max(0.75rem,calc(env(safe-area-inset-bottom,0px)+0.75rem))] bottom-2 left-2 right-2 sm:bottom-4 sm:left-auto sm:right-4 sm:items-end pointer-events-none"
      aria-label="Background jobs"
    >
      <div className="flex flex-col gap-2 items-stretch sm:items-end pointer-events-auto">
        {content}
      </div>
    </div>
  )

  return createPortal(shell, document.body)
}
